/**
 * AdminSystemStatus - Componente de Panel de Administración para Monitoreo del Sistema
 * 
 * Este componente proporciona una interfaz completa para monitorear el estado de todos los servicios
 * de la aplicación ColmaFácil, incluyendo:
 * 
 * - Servidor REST API: Estado, uptime, memoria, versión de Node.js
 * - Base de Datos: Estado de conexión, latencia, pool de conexiones, tamaño, tablas
 * - Almacenamiento (S3): Estado, latencia, bucket, región
 * - Email (SMTP): Estado, host, puerto
 * - Queue de Queries: Estado, tamaño de cola, queries pendientes
 * - WebSocket Server: Estado, conexiones activas, uptime, latencia, estado del cliente
 * - Métricas: Queries, errores, throughput, tiempos de respuesta
 * - Estadísticas: Usuarios, negocios, pedidos, productos
 * - Caché del Frontend: Uso, entradas válidas/expiradas, tamaño
 * - Rate Limiting: Límites y ventanas de tiempo
 * 
 * Características:
 * - Auto-actualización cada 30 segundos (configurable)
 * - Caché inteligente para reducir peticiones innecesarias
 * - Exportación de datos en formato JSON y CSV
 * - Indicadores visuales de estado (colores e iconos)
 * - Responsive design para todos los dispositivos
 * 
 * @component
 * @example
 * <AdminSystemStatus />
 */

import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import {
  Database,
  HardDrive,
  Mail,
  Server,
  Users,
  Store,
  ShoppingCart,
  Package,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  MemoryStick,
  Activity,
  Download,
  Wifi,
  WifiOff
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache, getCacheStats } from '../../lib/cache'
import { getSocket, isSocketConnected, getSocketId, connectWebSocket } from '../../lib/websocket' // Importar funciones de WebSocket

/**
 * Sistema de logging condicional
 * Por defecto NO muestra logs. Solo muestra cuando VITE_DEBUG_WEBSOCKET=true explícitamente
 */
const DEBUG_WEBSOCKET = import.meta.env.VITE_DEBUG_WEBSOCKET === 'true'
const IS_DEVELOPMENT = import.meta.env.DEV

// Por defecto, no mostrar logs a menos que esté explícitamente habilitado
const debugLog = (...args: any[]) => {
  // Solo mostrar si está explícitamente habilitado
  if (DEBUG_WEBSOCKET) {
    console.log('[AdminSystemStatus]', ...args)
  }
}

const debugWarn = (...args: any[]) => {
  // Solo mostrar si está explícitamente habilitado
  if (DEBUG_WEBSOCKET) {
    console.warn('[AdminSystemStatus]', ...args)
  }
}

const debugError = (...args: any[]) => {
  // Los errores solo se muestran en desarrollo
  if (IS_DEVELOPMENT) {
    console.error('[AdminSystemStatus]', ...args)
  }
}

interface SystemStatus {
  timestamp: string
  server: {
    status: string
    uptime: number
    environment: string
    version: string
    nodeVersion: string
    memory: {
      used: number
      total: number
      external: number
      rss: number
    }
  }
  services: {
    database: {
      status: string
      latency: number
      error?: string
      timestamp?: string
      pool?: {
        max: number
        min: number
        totalCount: number
        idleCount: number
        waitingCount: number
        activeCount: number
        usagePercent: number
      }
      details?: {
        size?: string
        sizeBytes?: number
        tables?: number
        version?: string
        error?: string
      }
    }
    storage: {
      status: string
      latency?: number
      error?: string
      bucket?: string
      endpoint?: string
      region?: string
    }
    email: {
      status: string
      error?: string
      host?: string
      port?: number
      message?: string
    }
    queue: {
      status: string
      size: number
      pending: number
      isPaused: boolean
      queueAvailable: boolean
      error?: string
    }
    websocket: {
      status: string
      connections?: number
      uptime?: number
      latency?: number
      clientConnected?: boolean
      socketId?: string | null
      error?: string
      timestamp?: string
    }
  }
  metrics?: {
    queries?: {
      total: number
      successful: number
      failed: number
      avgDuration: number
      errorRate: number
      throughput: number
      lastHour: number
      last24Hours: number
      slowestQueries: Array<{
        query: string
        duration: number
        timestamp: number
      }>
      error?: string
    }
    errors?: {
      recent: Array<{
        service: string
        message: string
        timestamp: string
        severity: string
        stack?: string
      }>
      byService: Record<string, number>
      trends: {
        lastHour: number
        last24Hours: number
        total: number
      }
      error?: string
    }
  }
  rateLimiting?: {
    max: number
    windowMs: number
    current: number
    error?: string
  }
  statistics: {
    users: {
      total: number
      active: number
    }
    stores: {
      total: number
      approved: number
      pending: number
      suspended: number
    }
    orders: {
      total: number
      today: number
      thisMonth: number
    }
    products: {
      total: number
      available: number
    }
  }
  overall: {
    status: string
    responseTime: number
  }
}

const AdminSystemStatus = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null)
  const [cacheStats, setCacheStats] = useState<any>(null)
  const [wsHealth, setWsHealth] = useState<any>(null)

  useEffect(() => {
    // Inicializar conexión WebSocket si el usuario está autenticado
    // Esto asegura que el socket esté disponible para verificación
    const initWebSocket = () => {
      const token = localStorage.getItem('token')
      if (token) {
        // Intentar obtener el socket (esto lo conectará si no está conectado)
        const socket = getSocket()
        if (socket && !socket.connected) {
          // Si el socket existe pero no está conectado, forzar conexión
          socket.connect()
        }
      }
    }
    
    // Inicializar WebSocket
    initWebSocket()
    
    // Cargar estado inicial del sistema y WebSocket
    // Dar un pequeño delay para que el socket tenga tiempo de conectar
    setTimeout(() => {
      loadSystemStatus()
      loadWebSocketHealth()
    }, 500) // Esperar 500ms para que el socket se conecte
    
    // Cargar estadísticas de caché del frontend
    setCacheStats(getCacheStats())

    // Suscribirse a actualizaciones en tiempo real del estado del sistema vía WebSocket
    const socket = getSocket()
    if (socket) {
      debugLog('Socket disponible, configurando listeners')
      
      // Suscribirse a actualizaciones del estado del sistema
      socket.emit('subscribe:system-status')
      debugLog('Suscrito a system:status:updated')
      
      // Handler para recibir actualizaciones del estado del sistema
      const handleSystemStatusUpdate = (updatedStatus: SystemStatus) => {
        debugLog('Actualización recibida vía WebSocket')
        
        // Actualizar el estado del sistema con los datos recibidos
        setStatus(updatedStatus)
        
        // Actualizar caché
        setCache('admin_system_status', updatedStatus, 30 * 1000)
        
        // Actualizar estadísticas de caché del frontend
        setCacheStats(getCacheStats())
        
        // Actualizar estado del WebSocket si viene en la actualización
        // Si no viene, actualizar con el estado actual del cliente
        if (updatedStatus.services?.websocket) {
          setWsHealth(updatedStatus.services.websocket)
        } else {
          // Si no viene en la actualización, obtener el estado actual del cliente
          const clientConnected = isSocketConnected()
          const socketId = getSocketId()
          const currentWsHealth = {
            status: clientConnected ? 'online' : 'offline',
            connections: updatedStatus.services?.websocket?.connections || (clientConnected ? 1 : 0),
            uptime: updatedStatus.services?.websocket?.uptime || 0,
            latency: updatedStatus.services?.websocket?.latency || 0,
            clientConnected: clientConnected,
            socketId: socketId,
            timestamp: new Date().toISOString()
          }
          setWsHealth(currentWsHealth)
          
          // Actualizar también en el estado del sistema
          setStatus((prevStatus) => {
            if (!prevStatus) {
              return {
                ...updatedStatus,
                services: {
                  ...updatedStatus.services,
                  websocket: currentWsHealth
                }
              }
            }
            return {
              ...updatedStatus,
              services: {
                ...updatedStatus.services,
                websocket: currentWsHealth
              }
            }
          })
        }
      }
      
      // Handler para recibir actualizaciones de la queue en tiempo real
      const handleQueueUpdate = (queueStats: any) => {
        debugLog('Actualización de queue recibida vía WebSocket:', {
          size: queueStats.size,
          pending: queueStats.pending,
          queueAvailable: queueStats.queueAvailable,
          isPaused: queueStats.isPaused
        })
        
        // Actualizar el estado de la queue en el estado del sistema
        // Usar función de actualización para asegurar que siempre se actualice
        setStatus((prevStatus) => {
          if (!prevStatus) {
            // Si no hay estado previo, crear uno mínimo con la queue
            return {
              timestamp: new Date().toISOString(),
              server: {
                status: 'online',
                uptime: 0,
                environment: 'development',
                version: '1.0.0',
                nodeVersion: 'unknown',
                memory: { used: 0, total: 0, external: 0, rss: 0 }
              },
              services: {
                database: { status: 'unknown', latency: 0 },
                storage: { status: 'unknown' },
                email: { status: 'unknown' },
                queue: {
                  status: queueStats.queueAvailable ? 'online' : 'fallback',
                  size: queueStats.size || 0,
                  pending: queueStats.pending || 0,
                  isPaused: queueStats.isPaused || false,
                  queueAvailable: queueStats.queueAvailable || false,
                  error: queueStats.error || undefined
                },
                websocket: { status: 'unknown' }
              },
              statistics: {
                users: { total: 0, active: 0 },
                stores: { total: 0, approved: 0, pending: 0, suspended: 0 },
                orders: { total: 0, today: 0, thisMonth: 0 },
                products: { total: 0, available: 0 }
              },
              overall: { status: 'unknown', responseTime: 0 }
            }
          }
          
          // Si hay estado previo, actualizar solo la queue
          const updatedQueue = {
            status: queueStats.queueAvailable ? 'online' : 'fallback',
            size: queueStats.size || 0,
            pending: queueStats.pending || 0,
            isPaused: queueStats.isPaused || false,
            queueAvailable: queueStats.queueAvailable || false,
            error: queueStats.error || undefined
          }
          
          debugLog('Actualizando queue en estado:', {
            anterior: {
              size: prevStatus.services.queue.size,
              pending: prevStatus.services.queue.pending
            },
            nuevo: {
              size: updatedQueue.size,
              pending: updatedQueue.pending
            }
          })
          
          return {
            ...prevStatus,
            services: {
              ...prevStatus.services,
              queue: updatedQueue
            }
          }
        })
      }
      
      // Escuchar actualizaciones del estado del sistema
      socket.on('system:status:updated', handleSystemStatusUpdate)
      debugLog('Listener para system:status:updated configurado')
      
      // Escuchar actualizaciones de la queue en tiempo real
      socket.on('queue:updated', handleQueueUpdate)
      debugLog('Listener para queue:updated configurado')
      
      // Verificar que el socket esté conectado
      if (socket.connected) {
        debugLog('Socket conectado, ID:', socket.id)
      } else {
        debugWarn('Socket no está conectado aún')
        socket.on('connect', () => {
          debugLog('Socket conectado después de esperar, ID:', socket.id)
        })
      }
      
      // Cleanup: desuscribirse cuando el componente se desmonte
      return () => {
        socket.off('system:status:updated', handleSystemStatusUpdate)
        socket.off('queue:updated', handleQueueUpdate)
        socket.emit('unsubscribe:system-status')
      }
    }

    // NOTA: Ya no usamos polling de 30 segundos, ahora usamos WebSocket en tiempo real
    // El auto-refresh ahora solo actualiza el caché del frontend periódicamente
    if (autoRefresh) {
      const interval = window.setInterval(() => {
        // Solo actualizar caché del frontend, no hacer polling del servidor
        setCacheStats(getCacheStats())
      }, 30000) // Actualizar caché cada 30 segundos
      setRefreshInterval(interval)

      return () => {
        if (interval) window.clearInterval(interval)
      }
    } else if (refreshInterval) {
      window.clearInterval(refreshInterval)
      setRefreshInterval(null)
    }
  }, [autoRefresh])

  /**
   * Carga el estado del sistema desde el backend REST API
   * @param showToast - Si es true, muestra un toast de éxito/error
   */
  const loadSystemStatus = async (showToast = true) => {
    const cacheKey = 'admin_system_status'
    
    // Intentar cargar desde cache primero (solo si no hay auto-refresh activo o es la primera carga)
    if (!autoRefresh || !status) {
      const cachedStatus = getCacheStale<SystemStatus>(cacheKey)
      if (cachedStatus) {
        setStatus(cachedStatus)
        setLoading(false)
      }
    }

    try {
      setLoading(true)
      const response = await api.getSystemStatus()
      if (response.success) {
        setStatus(response.data)
        
        // Guardar en cache (30 segundos de TTL - muy dinámico pero útil para errores)
        setCache(cacheKey, response.data, 30 * 1000)
        
        if (showToast) {
          toast.success('Estado del sistema actualizado')
        }
      } else {
        throw new Error(response.message || 'Error al cargar estado')
      }
        } catch (error: any) {
          debugError('Error loading system status:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && status) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      if (showToast) {
        toast.error('Error al cargar el estado del sistema')
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Carga el estado de salud del servidor WebSocket
   * Verifica la conectividad, número de conexiones activas, uptime y latencia
   * @param showToast - Si es true, muestra un toast de éxito/error
   */
  const loadWebSocketHealth = async (showToast = true) => {
    const cacheKey = 'admin_websocket_health'
    const startTime = Date.now()
    
    try {
      // Obtener estado del servidor WebSocket mediante health check
      const response = await api.getWebSocketHealth()
      
      debugLog('Respuesta del health check recibida')
      
      // Obtener el socket y verificar estado del cliente ANTES de procesar la respuesta
      // Esto nos permite mostrar el estado correcto incluso si el health check falla
      const socket = getSocket()
      let clientConnected = isSocketConnected()
      let socketId = getSocketId()
      
      // Si el socket existe pero aún no está conectado, darle un momento
      if (socket && !clientConnected) {
        debugLog('Socket existe pero no está conectado, esperando conexión...')
        for (let i = 0; i < 3 && !clientConnected; i++) {
          await new Promise(resolve => setTimeout(resolve, 200))
          clientConnected = isSocketConnected()
          socketId = getSocketId()
          if (clientConnected) {
            debugLog('Socket conectado después de esperar')
            break
          }
        }
      }
      
      if (response.success) {
        // Calcular latencia del health check
        const latency = Date.now() - startTime
        
        debugLog('Estado final del WebSocket:', {
          clientConnected,
          socketId,
          serverConnections: response.data.connections,
          healthCheckSuccess: true
        })
        
        // response.data.status ya viene como 'online' desde api.ts (convertido de 'ok')
        const wsHealthData = {
          status: response.data.status || 'online', // Ya viene como 'online' desde api.ts
          connections: response.data.connections || 0,
          uptime: response.data.uptime || 0,
          latency: latency,
          clientConnected: clientConnected,
          socketId: socketId,
          timestamp: response.data.timestamp || new Date().toISOString(),
          error: response.data.error || undefined
        }
        
        debugLog('wsHealthData creado')
        
        setWsHealth(wsHealthData)
        
        // Guardar en cache (15 segundos de TTL - el WebSocket cambia más frecuentemente)
        setCache(cacheKey, wsHealthData, 15 * 1000)
        
        // Actualizar el estado del sistema con la información del WebSocket
        // IMPORTANTE: Actualizar siempre, incluso si status es null (se inicializará)
        setStatus((prevStatus) => {
          if (!prevStatus) {
            // Si no hay estado previo, crear uno mínimo con el WebSocket
            return {
              timestamp: new Date().toISOString(),
              server: {
                status: 'online',
                uptime: 0,
                environment: 'development',
                version: '1.0.0',
                nodeVersion: 'unknown',
                memory: { used: 0, total: 0, external: 0, rss: 0 }
              },
              services: {
                database: { status: 'unknown', latency: 0 },
                storage: { status: 'unknown' },
                email: { status: 'unknown' },
                queue: { status: 'unknown', size: 0, pending: 0, isPaused: false, queueAvailable: false },
                websocket: wsHealthData
              },
              statistics: {
                users: { total: 0, active: 0 },
                stores: { total: 0, approved: 0, pending: 0, suspended: 0 },
                orders: { total: 0, today: 0, thisMonth: 0 },
                products: { total: 0, available: 0 }
              },
              overall: { status: 'unknown', responseTime: 0 }
            }
          }
          // Si hay estado previo, actualizar solo el WebSocket
          return {
            ...prevStatus,
            services: {
              ...prevStatus.services,
              websocket: wsHealthData
            }
          }
        })
        
        if (showToast && response.data.status === 'online') {
          toast.success('WebSocket conectado correctamente')
        }
      } else {
        // El health check falló, pero verificar si el cliente está conectado
        // Si el cliente está conectado, el servidor probablemente está funcionando
        // pero el health check HTTP puede estar fallando por CORS u otro motivo
        const latency = Date.now() - startTime
        
            debugLog('Health check falló, pero cliente conectado:', {
              clientConnected,
              socketId,
              healthCheckError: response.data.error
            })
        
        // Si el cliente está conectado, considerar el servidor como online
        // aunque el health check HTTP haya fallado
        const wsHealthData = {
          status: clientConnected ? 'online' : 'offline',
          connections: clientConnected ? 1 : 0, // Si el cliente está conectado, al menos hay 1 conexión
          uptime: 0, // No disponible sin health check
          latency: latency,
          clientConnected: clientConnected,
          socketId: socketId,
          error: clientConnected 
            ? 'Health check HTTP falló, pero cliente conectado' 
            : (response.data.error || 'Servidor WebSocket no disponible'),
          timestamp: new Date().toISOString(),
          healthCheckFailed: true // Marcar que el health check falló
        }
        
        setWsHealth(wsHealthData)
        
        // Actualizar el estado del sistema
        // IMPORTANTE: Actualizar siempre, incluso si status es null
        setStatus((prevStatus) => {
          if (!prevStatus) {
            // Si no hay estado previo, crear uno mínimo con el WebSocket
            return {
              timestamp: new Date().toISOString(),
              server: {
                status: 'online',
                uptime: 0,
                environment: 'development',
                version: '1.0.0',
                nodeVersion: 'unknown',
                memory: { used: 0, total: 0, external: 0, rss: 0 }
              },
              services: {
                database: { status: 'unknown', latency: 0 },
                storage: { status: 'unknown' },
                email: { status: 'unknown' },
                queue: { status: 'unknown', size: 0, pending: 0, isPaused: false, queueAvailable: false },
                websocket: wsHealthData
              },
              statistics: {
                users: { total: 0, active: 0 },
                stores: { total: 0, approved: 0, pending: 0, suspended: 0 },
                orders: { total: 0, today: 0, thisMonth: 0 },
                products: { total: 0, available: 0 }
              },
              overall: { status: 'unknown', responseTime: 0 }
            }
          }
          // Si hay estado previo, actualizar solo el WebSocket
          return {
            ...prevStatus,
            services: {
              ...prevStatus.services,
              websocket: wsHealthData
            }
          }
        })
        
        if (showToast) {
          if (clientConnected) {
            toast.success('WebSocket conectado (health check HTTP no disponible)')
          } else {
            toast.error('WebSocket no disponible')
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading WebSocket health:', error)
      
      // En caso de error, intentar usar datos del cache
      const cachedHealth = getCacheStale<any>(cacheKey)
      if (cachedHealth) {
        setWsHealth(cachedHealth)
        if (status) {
          setStatus({
            ...status,
            services: {
              ...status.services,
              websocket: cachedHealth
            }
          })
        }
        return
      }
      
      // Si no hay cache, establecer estado offline
      const wsHealthData = {
        status: 'offline',
        connections: 0,
        uptime: 0,
        latency: Date.now() - startTime,
        clientConnected: isSocketConnected(),
        error: error.message || 'Error al verificar estado del WebSocket',
        timestamp: new Date().toISOString()
      }
      
      setWsHealth(wsHealthData)
      
      if (status) {
        setStatus({
          ...status,
          services: {
            ...status.services,
            websocket: wsHealthData
          }
        })
      }
      
      if (showToast) {
        toast.error('Error al verificar estado del WebSocket')
      }
    }
  }

  /**
   * Obtiene las clases CSS para el color del estado según el valor del status
   * @param status - Estado del servicio ('online', 'offline', 'healthy', 'error', etc.)
   * @returns String con las clases de Tailwind CSS para el color correspondiente
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'configured':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'error':
      case 'offline':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'degraded':
      case 'not_configured':
      case 'unknown':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  /**
   * Obtiene el icono correspondiente al estado del servicio
   * @param status - Estado del servicio ('online', 'offline', 'healthy', 'error', etc.)
   * @returns Componente de icono de Lucide React
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'configured':
        return <CheckCircle className="w-5 h-5" />
      case 'error':
      case 'offline':
        return <XCircle className="w-5 h-5" />
      case 'degraded':
      case 'not_configured':
      case 'unknown':
        return <AlertCircle className="w-5 h-5" />
      default:
        return <Clock className="w-5 h-5" />
    }
  }

  /**
   * Formatea el tiempo de actividad (uptime) en segundos a un formato legible
   * @param seconds - Tiempo en segundos
   * @returns String formateado (ej: "2d 5h 30m", "3h 15m", "45m")
   */
  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  /**
   * Descarga el estado completo del sistema en formato JSON
   * Incluye toda la información de servicios, métricas, estadísticas y caché del frontend
   * El archivo se descarga automáticamente con un nombre que incluye la fecha y timestamp
   */
  const downloadJSON = () => {
    if (!status) return

    // Preparar datos para exportación incluyendo caché del frontend
    const exportData = {
      ...status,
      cache: cacheStats,
      websocketHealth: wsHealth, // Incluir estado detallado del WebSocket
      exportedAt: new Date().toISOString(),
      exportedBy: 'System Admin'
    }

    // Convertir a JSON con formato legible (2 espacios de indentación)
    const dataStr = JSON.stringify(exportData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    // Crear enlace temporal para descarga
    const link = document.createElement('a')
    link.href = url
    link.download = `system-status-${new Date().toISOString().split('T')[0]}-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url) // Limpiar URL del objeto
    
    toast.success('Información descargada en formato JSON')
  }

  /**
   * Descarga el estado del sistema en formato CSV
   * Formatea toda la información en un formato tabular compatible con Excel y hojas de cálculo
   * Incluye secciones para: Servidor, Servicios, Métricas, Errores, Estadísticas, Caché, Rate Limiting
   */
  const downloadCSV = () => {
    if (!status) return

    let csv = 'Estado del Sistema - Reporte\n'
    csv += `Fecha de exportación,${new Date().toISOString()}\n\n`

    // Servidor
    csv += '=== SERVIDOR ===\n'
    csv += `Estado,${status.server.status}\n`
    csv += `Tiempo activo,${formatUptime(status.server.uptime)}\n`
    csv += `Entorno,${status.server.environment}\n`
    csv += `Versión,${status.server.version}\n`
    csv += `Node.js,${status.server.nodeVersion}\n`
    csv += `Memoria usada (MB),${status.server.memory.used}\n`
    csv += `Memoria total (MB),${status.server.memory.total}\n\n`

    // Servicios
    csv += '=== SERVICIOS ===\n'
    csv += `Base de Datos - Estado,${status.services.database.status}\n`
    csv += `Base de Datos - Latencia (ms),${status.services.database.latency}\n`
    if (status.services.database.pool) {
      csv += `Pool - Máximo,${status.services.database.pool.max}\n`
      csv += `Pool - Activas,${status.services.database.pool.activeCount}\n`
      csv += `Pool - Idle,${status.services.database.pool.idleCount}\n`
      csv += `Pool - Esperando,${status.services.database.pool.waitingCount}\n`
      csv += `Pool - Uso (%),${status.services.database.pool.usagePercent}\n`
    }
    if (status.services.database.details) {
      csv += `BD - Tamaño,${status.services.database.details.size || 'N/A'}\n`
      csv += `BD - Tablas,${status.services.database.details.tables || 'N/A'}\n`
      csv += `BD - Versión,${status.services.database.details.version || 'N/A'}\n`
    }
    csv += `Almacenamiento - Estado,${status.services.storage.status}\n`
    csv += `Email - Estado,${status.services.email.status}\n`
    csv += `Queue - Estado,${status.services.queue.status}\n`
    csv += `Queue - En cola,${status.services.queue.size}\n`
    csv += `Queue - Ejecutándose,${status.services.queue.pending}\n`
    csv += `WebSocket - Estado,${status.services.websocket?.status || 'unknown'}\n`
    csv += `WebSocket - Conexiones activas,${status.services.websocket?.connections || 0}\n`
    csv += `WebSocket - Cliente conectado,${status.services.websocket?.clientConnected ? 'Sí' : 'No'}\n`
    if (status.services.websocket?.uptime) {
      csv += `WebSocket - Tiempo activo,${formatUptime(status.services.websocket.uptime)}\n`
    }
    if (status.services.websocket?.latency) {
      csv += `WebSocket - Latencia (ms),${status.services.websocket.latency}\n`
    }
    if (status.services.websocket?.error) {
      csv += `WebSocket - Error,${status.services.websocket.error}\n`
    }
    csv += '\n'

    // Métricas
    if (status.metrics?.queries) {
      csv += '=== MÉTRICAS DE QUERIES ===\n'
      csv += `Total,${status.metrics.queries.total}\n`
      csv += `Exitosas,${status.metrics.queries.successful}\n`
      csv += `Fallidas,${status.metrics.queries.failed}\n`
      csv += `Tiempo promedio (ms),${status.metrics.queries.avgDuration}\n`
      csv += `Tasa de error (%),${status.metrics.queries.errorRate}\n`
      csv += `Throughput (q/s),${status.metrics.queries.throughput}\n`
      csv += `Última hora,${status.metrics.queries.lastHour}\n`
      csv += `Últimas 24h,${status.metrics.queries.last24Hours}\n\n`
    }

    // Errores
    if (status.metrics?.errors) {
      csv += '=== ERRORES ===\n'
      csv += `Última hora,${status.metrics.errors.trends.lastHour}\n`
      csv += `Últimas 24h,${status.metrics.errors.trends.last24Hours}\n`
      csv += `Total,${status.metrics.errors.trends.total}\n`
      csv += '\nErrores por servicio:\n'
      Object.entries(status.metrics.errors.byService).forEach(([service, count]) => {
        csv += `${service},${count}\n`
      })
      csv += '\n'
    }

    // Estadísticas
    csv += '=== ESTADÍSTICAS ===\n'
    csv += `Usuarios - Total,${status.statistics.users.total}\n`
    csv += `Usuarios - Activos,${status.statistics.users.active}\n`
    csv += `Negocios - Total,${status.statistics.stores.total}\n`
    csv += `Negocios - Aprobados,${status.statistics.stores.approved}\n`
    csv += `Negocios - Pendientes,${status.statistics.stores.pending}\n`
    csv += `Pedidos - Total,${status.statistics.orders.total}\n`
    csv += `Pedidos - Hoy,${status.statistics.orders.today}\n`
    csv += `Pedidos - Este mes,${status.statistics.orders.thisMonth}\n`
    csv += `Productos - Total,${status.statistics.products.total}\n`
    csv += `Productos - Disponibles,${status.statistics.products.available}\n\n`

    // Caché
    if (cacheStats) {
      csv += '=== CACHÉ (FRONTEND) ===\n'
      csv += `Total entradas,${cacheStats.totalEntries}\n`
      csv += `Entradas válidas,${cacheStats.validEntries}\n`
      csv += `Entradas expiradas,${cacheStats.expiredEntries}\n`
      csv += `Tamaño (KB),${cacheStats.sizeKB}\n`
      csv += `Tamaño (MB),${cacheStats.sizeMB}\n`
      csv += `Uso (%),${cacheStats.usagePercent}\n\n`
    }

    // Rate Limiting
    if (status.rateLimiting) {
      csv += '=== RATE LIMITING ===\n'
      csv += `Límite,${status.rateLimiting.max}\n`
      csv += `Ventana (minutos),${status.rateLimiting.windowMs / 1000 / 60}\n\n`
    }

    const dataBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `system-status-${new Date().toISOString().split('T')[0]}-${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast.success('Información descargada en formato CSV')
  }


  if (loading && !status) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AdminNavHeader
          title="Estado del Sistema"
          subtitle="Monitoreo de servicios y conexiones"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-gray-600">Cargando estado del sistema...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!status) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AdminNavHeader
          title="Estado del Sistema"
          subtitle="Monitoreo de servicios y conexiones"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error al cargar estado</h3>
            <p className="text-gray-600 mb-4">No se pudo obtener el estado del sistema</p>
            <button
              onClick={() => loadSystemStatus()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <AdminNavHeader
        title="Estado del Sistema"
        subtitle="Monitoreo de servicios y conexiones"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={() => {
                loadSystemStatus()
                loadWebSocketHealth()
              }}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Actualización en tiempo real (WebSocket)</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadJSON}
                disabled={!status}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Descargar como JSON"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">JSON</span>
              </button>
              <button
                onClick={downloadCSV}
                disabled={!status}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Descargar como CSV"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">CSV</span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              {wsHealth?.clientConnected && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="WebSocket conectado"></div>
              )}
              <span>Última actualización: {new Date(status.timestamp).toLocaleTimeString('es-DO')}</span>
            </div>
            {wsHealth?.clientConnected && (
              <span className="text-xs text-green-600">(Tiempo real)</span>
            )}
          </div>
        </div>

        {/* Overall Status */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Estado General</h2>
              <p className="text-gray-600">Tiempo de respuesta: {status.overall.responseTime}ms</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getStatusColor(status.overall.status)}`}>
              {getStatusIcon(status.overall.status)}
              <span className="font-semibold capitalize">
                {status.overall.status === 'healthy' ? 'Saludable' : 'Degradado'}
              </span>
            </div>
          </div>
        </div>

        {/* Services Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Database */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Database className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Base de Datos</h3>
                  <p className="text-xs text-gray-500">PostgreSQL</p>
                </div>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${getStatusColor(status.services.database.status)}`}>
                {getStatusIcon(status.services.database.status)}
                <span className="capitalize">{status.services.database.status}</span>
              </div>
            </div>
            {status.services.database.status === 'online' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Latencia:</span>
                  <span className="font-medium">{status.services.database.latency}ms</span>
                </div>
                {status.services.database.pool && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pool:</span>
                      <span className="font-medium">
                        {status.services.database.pool.activeCount}/{status.services.database.pool.max}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Uso:</span>
                      <span className={`font-medium ${status.services.database.pool.usagePercent > 80 ? 'text-red-600' : status.services.database.pool.usagePercent > 60 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {status.services.database.pool.usagePercent}%
                      </span>
                    </div>
                    {status.services.database.pool.waitingCount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-yellow-600">Esperando:</span>
                        <span className="font-medium text-yellow-600">{status.services.database.pool.waitingCount}</span>
                      </div>
                    )}
                  </>
                )}
                {status.services.database.details && (
                  <>
                    {status.services.database.details.size && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tamaño BD:</span>
                        <span className="font-medium text-xs">{status.services.database.details.size}</span>
                      </div>
                    )}
                    {status.services.database.details.tables && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tablas:</span>
                        <span className="font-medium">{status.services.database.details.tables}</span>
                      </div>
                    )}
                    {status.services.database.details.version && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Versión:</span>
                        <span className="font-medium text-xs">{status.services.database.details.version}</span>
                      </div>
                    )}
                  </>
                )}
                {status.services.database.timestamp && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Última conexión:</span>
                    <span className="font-medium text-xs">
                      {new Date(status.services.database.timestamp).toLocaleTimeString('es-DO')}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                {status.services.database.error || 'Error de conexión'}
              </div>
            )}
          </div>

          {/* Storage */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <HardDrive className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Almacenamiento</h3>
                  <p className="text-xs text-gray-500">IDrive E2</p>
                </div>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${getStatusColor(status.services.storage.status)}`}>
                {getStatusIcon(status.services.storage.status)}
                <span className="capitalize">
                  {status.services.storage.status === 'not_configured' 
                    ? 'No configurado' 
                    : status.services.storage.status === 'unknown'
                    ? 'Desconocido'
                    : status.services.storage.status}
                </span>
              </div>
            </div>
            {status.services.storage.status === 'online' ? (
              <div className="space-y-2 text-sm">
                {status.services.storage.latency && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Latencia:</span>
                    <span className="font-medium">{status.services.storage.latency}ms</span>
                  </div>
                )}
                {status.services.storage.bucket && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bucket:</span>
                    <span className="font-medium text-xs truncate ml-2">{status.services.storage.bucket}</span>
                  </div>
                )}
                {status.services.storage.region && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Región:</span>
                    <span className="font-medium">{status.services.storage.region}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                {status.services.storage.error || 'Error de conexión'}
              </div>
            )}
          </div>

          {/* Email */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email</h3>
                  <p className="text-xs text-gray-500">SMTP</p>
                </div>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${getStatusColor(status.services.email.status)}`}>
                {getStatusIcon(status.services.email.status)}
                <span className="capitalize">
                  {status.services.email.status === 'not_configured' ? 'No configurado' : status.services.email.status}
                </span>
              </div>
            </div>
            {status.services.email.status === 'configured' ? (
              <div className="space-y-2 text-sm">
                {status.services.email.host && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Host:</span>
                    <span className="font-medium text-xs truncate ml-2">{status.services.email.host}</span>
                  </div>
                )}
                {status.services.email.port && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Puerto:</span>
                    <span className="font-medium">{status.services.email.port}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                {status.services.email.message || status.services.email.error || 'No configurado'}
              </div>
            )}
          </div>

          {/* Queue */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Queue de Queries</h3>
                  <p className="text-xs text-gray-500">p-queue</p>
                </div>
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${getStatusColor(status.services.queue.status)}`}>
                {getStatusIcon(status.services.queue.status)}
                <span className="capitalize">
                  {status.services.queue.status === 'fallback' ? 'Fallback' : status.services.queue.status}
                </span>
              </div>
            </div>
            {status.services.queue.queueAvailable ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">En cola:</span>
                  <span className="font-medium">{status.services.queue.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Ejecutándose:</span>
                  <span className="font-medium">{status.services.queue.pending}</span>
                </div>
                {status.services.queue.isPaused && (
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Estado:</span>
                    <span className="font-medium text-yellow-600">Pausada</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="text-yellow-600 font-medium">Modo Fallback</div>
                <div className="text-xs text-gray-600">
                  {status.services.queue.error || 'Las queries se ejecutan directamente sin queue'}
                </div>
              </div>
            )}
          </div>

          {/* 
            WebSocket Server - Tarjeta de Monitoreo del Servidor WebSocket
            Muestra el estado completo del servicio WebSocket incluyendo:
            - Estado del servidor (online/offline)
            - Número de conexiones activas en el servidor
            - Estado de conexión del cliente en el frontend
            - ID del socket del cliente (si está conectado)
            - Uptime del servidor WebSocket
            - Latencia del health check (con colores según el tiempo de respuesta)
            - Timestamp de última verificación
          */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Icono dinámico: Wifi si está online, WifiOff si está offline */}
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                  {(() => {
                    const wsStatus = wsHealth?.status || status.services.websocket?.status
                    return wsStatus === 'online' ? (
                      <Wifi className="w-6 h-6 text-cyan-600" />
                    ) : (
                      <WifiOff className="w-6 h-6 text-red-600" />
                    )
                  })()}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">WebSocket Server</h3>
                  <p className="text-xs text-gray-500">Socket.IO</p>
                </div>
              </div>
              {/* Badge de estado con color y icono correspondiente */}
              {(() => {
                const wsStatus = wsHealth?.status || status.services.websocket?.status || 'unknown'
                return (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${getStatusColor(wsStatus)}`}>
                    {getStatusIcon(wsStatus)}
                    <span className="capitalize">
                      {wsStatus === 'online' 
                        ? 'En línea' 
                        : wsStatus === 'offline'
                        ? 'Desconectado'
                        : 'Desconocido'}
                    </span>
                  </div>
                )
              })()}
            </div>
            
            {/* Contenido cuando el servidor WebSocket está online */}
            {/* Usar wsHealth como fuente de verdad si está disponible, sino usar status.services.websocket */}
            {(wsHealth?.status === 'online' || status.services.websocket?.status === 'online') ? (
              <div className="space-y-2 text-sm">
                {/* Usar wsHealth como fuente de verdad si está disponible, sino usar status.services.websocket */}
                {(() => {
                  const wsData = wsHealth || status.services.websocket
                  return (
                    <>
                      {/* Conexiones activas: Número total de clientes conectados al servidor WebSocket */}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conexiones activas:</span>
                        <span className="font-medium text-cyan-600">
                          {wsData?.connections || 0}
                        </span>
                      </div>
                      
                      {/* Estado de conexión del cliente: Si el frontend actual está conectado al WebSocket */}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cliente conectado:</span>
                        <span className={`font-medium ${wsData?.clientConnected ? 'text-green-600' : 'text-red-600'}`}>
                          {wsData?.clientConnected ? 'Sí' : 'No'}
                        </span>
                      </div>
                      
                      {/* Socket ID: Identificador único de la conexión del cliente (solo si está conectado) */}
                      {wsData?.clientConnected && wsData?.socketId && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Socket ID:</span>
                          <span className="font-medium text-xs truncate ml-2" title={wsData.socketId}>
                            {wsData.socketId.substring(0, 12)}...
                          </span>
                        </div>
                      )}
                      
                      {/* Uptime: Tiempo que el servidor WebSocket ha estado activo (formateado) */}
                      {wsData?.uptime !== undefined && wsData.uptime > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tiempo activo:</span>
                          <span className="font-medium">{formatUptime(wsData.uptime)}</span>
                        </div>
                      )}
                      
                      {/* Latencia: Tiempo de respuesta del health check (colores: verde <100ms, amarillo <500ms, rojo >=500ms) */}
                      {wsData?.latency !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Latencia:</span>
                          <span className={`font-medium ${
                            wsData.latency < 100 
                              ? 'text-green-600' 
                              : wsData.latency < 500 
                              ? 'text-yellow-600' 
                              : 'text-red-600'
                          }`}>
                            {wsData.latency}ms
                          </span>
                        </div>
                      )}
                      
                      {/* Timestamp: Fecha y hora de la última verificación del estado */}
                      {wsData?.timestamp && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Última verificación:</span>
                          <span className="font-medium text-xs">
                            {new Date(wsData.timestamp).toLocaleTimeString('es-DO')}
                          </span>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            ) : (
              /* Contenido cuando el servidor WebSocket está offline o no disponible */
              <div className="space-y-2 text-sm">
                {/* Mensaje de error: Muestra el motivo por el cual el servidor no está disponible */}
                <div className="text-red-600 font-medium">
                  {status.services.websocket?.error || 'Servidor WebSocket no disponible'}
                </div>
                
                {/* Indicador de reconexión: Muestra si el cliente está intentando reconectar automáticamente */}
                {status.services.websocket?.clientConnected && (
                  <div className="text-yellow-600 text-xs">
                    Cliente intentando reconectar...
                  </div>
                )}
                
                {/* Timestamp: Última verificación incluso cuando está offline (útil para debugging) */}
                {status.services.websocket?.timestamp && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Última verificación:</span>
                    <span>
                      {new Date(status.services.websocket.timestamp).toLocaleTimeString('es-DO')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Server Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Server Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Servidor</h3>
                <p className="text-xs text-gray-500">Información del sistema</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Estado:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(status.server.status)}`}>
                  {status.server.status === 'online' ? 'En línea' : 'Desconectado'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tiempo activo:</span>
                <span className="font-medium">{formatUptime(status.server.uptime)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Entorno:</span>
                <span className="font-medium capitalize">{status.server.environment}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Versión:</span>
                <span className="font-medium">{status.server.version}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Node.js:</span>
                <span className="font-medium">{status.server.nodeVersion}</span>
              </div>
            </div>
          </div>

          {/* Memory Usage */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <MemoryStick className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Memoria</h3>
                <p className="text-xs text-gray-500">Uso de recursos</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Usada:</span>
                  <span className="text-sm font-medium">{status.server.memory.used} MB</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${(status.server.memory.used / status.server.memory.total) * 100}%` }}
                  />
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total:</span>
                <span className="font-medium">{status.server.memory.total} MB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">RSS:</span>
                <span className="font-medium">{status.server.memory.rss} MB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">External:</span>
                <span className="font-medium">{status.server.memory.external} MB</span>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics and Performance */}
        {status.metrics && (status.metrics.queries || status.metrics.errors) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Query Metrics */}
            {status.metrics.queries && !status.metrics.queries.error && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Métricas de Queries</h3>
                    <p className="text-xs text-gray-500">Rendimiento de base de datos</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Total</div>
                      <div className="text-2xl font-bold text-gray-900">{status.metrics.queries.total.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Exitosas</div>
                      <div className="text-2xl font-bold text-green-600">{status.metrics.queries.successful.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Fallidas</div>
                      <div className="text-2xl font-bold text-red-600">{status.metrics.queries.failed.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Tasa de Error</div>
                      <div className="text-2xl font-bold text-orange-600">{status.metrics.queries.errorRate}%</div>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-gray-200 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tiempo promedio:</span>
                      <span className="font-medium">{status.metrics.queries.avgDuration}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Throughput:</span>
                      <span className="font-medium">{status.metrics.queries.throughput} q/s</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Última hora:</span>
                      <span className="font-medium">{status.metrics.queries.lastHour}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Últimas 24h:</span>
                      <span className="font-medium">{status.metrics.queries.last24Hours}</span>
                    </div>
                  </div>
                  {status.metrics.queries.slowestQueries && status.metrics.queries.slowestQueries.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Queries más lentas:</div>
                      <div className="space-y-1">
                        {status.metrics.queries.slowestQueries.slice(0, 3).map((q, idx) => (
                          <div key={idx} className="text-xs text-gray-600 truncate" title={q.query}>
                            {q.duration}ms - {q.query.substring(0, 50)}...
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error Metrics */}
            {status.metrics.errors && !status.metrics.errors.error && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Errores Recientes</h3>
                    <p className="text-xs text-gray-500">Monitoreo de errores</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Última hora</div>
                      <div className="text-xl font-bold text-orange-600">{status.metrics.errors.trends.lastHour}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Últimas 24h</div>
                      <div className="text-xl font-bold text-red-600">{status.metrics.errors.trends.last24Hours}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Total</div>
                      <div className="text-xl font-bold text-gray-900">{status.metrics.errors.trends.total}</div>
                    </div>
                  </div>
                  {Object.keys(status.metrics.errors.byService).length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Por servicio:</div>
                      <div className="space-y-1">
                        {Object.entries(status.metrics.errors.byService).map(([service, count]) => (
                          <div key={service} className="flex justify-between text-xs">
                            <span className="text-gray-600 capitalize">{service}:</span>
                            <span className="font-medium text-red-600">{count as number}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {status.metrics.errors.recent && status.metrics.errors.recent.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Últimos errores:</div>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {status.metrics.errors.recent.slice(0, 5).map((error, idx) => (
                          <div key={idx} className="text-xs p-2 bg-red-50 rounded border border-red-100">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-red-800 capitalize">{error.service}</span>
                              <span className="text-gray-500 text-xs">
                                {new Date(error.timestamp).toLocaleTimeString('es-DO')}
                              </span>
                            </div>
                            <div className="text-gray-700 truncate" title={error.message}>
                              {error.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cache and Rate Limiting */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Cache Stats */}
          {cacheStats && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <HardDrive className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Caché (Frontend)</h3>
                  <p className="text-xs text-gray-500">localStorage</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">Uso:</span>
                    <span className="text-sm font-medium">{cacheStats.sizeMB} MB / {cacheStats.estimatedLimitMB} MB</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${cacheStats.usagePercent > 80 ? 'bg-red-600' : cacheStats.usagePercent > 60 ? 'bg-yellow-600' : 'bg-green-600'}`}
                      style={{ width: `${cacheStats.usagePercent}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{cacheStats.usagePercent}% usado</div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total entradas</div>
                    <div className="text-lg font-bold text-gray-900">{cacheStats.totalEntries}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Válidas</div>
                    <div className="text-lg font-bold text-green-600">{cacheStats.validEntries}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Expiradas</div>
                    <div className="text-lg font-bold text-yellow-600">{cacheStats.expiredEntries}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Tamaño</div>
                    <div className="text-lg font-bold text-gray-900">{cacheStats.sizeKB} KB</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rate Limiting */}
          {status.rateLimiting && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Rate Limiting</h3>
                  <p className="text-xs text-gray-500">Límite de requests</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Límite:</span>
                  <span className="font-medium">{status.rateLimiting.max} requests</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Ventana:</span>
                  <span className="font-medium">{status.rateLimiting.windowMs / 1000 / 60} minutos</span>
                </div>
                {status.rateLimiting.current !== undefined && status.rateLimiting.current >= 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Actual:</span>
                    <span className="font-medium">{status.rateLimiting.current}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Users */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Usuarios</h3>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-gray-900">{status.statistics.users.total.toLocaleString()}</div>
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">{status.statistics.users.active}</span> activos
              </div>
            </div>
          </div>

          {/* Stores */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Store className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Negocios</h3>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-gray-900">{status.statistics.stores.total.toLocaleString()}</div>
              <div className="text-sm text-gray-600 space-y-1">
                <div><span className="text-green-600 font-medium">{status.statistics.stores.approved}</span> aprobados</div>
                <div><span className="text-yellow-600 font-medium">{status.statistics.stores.pending}</span> pendientes</div>
                {status.statistics.stores.suspended > 0 && (
                  <div><span className="text-red-600 font-medium">{status.statistics.stores.suspended}</span> suspendidos</div>
                )}
              </div>
            </div>
          </div>

          {/* Orders */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Pedidos</h3>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-gray-900">{status.statistics.orders.total.toLocaleString()}</div>
              <div className="text-sm text-gray-600 space-y-1">
                <div><span className="font-medium">{status.statistics.orders.today}</span> hoy</div>
                <div><span className="font-medium">{status.statistics.orders.thisMonth}</span> este mes</div>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Productos</h3>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-gray-900">{status.statistics.products.total.toLocaleString()}</div>
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">{status.statistics.products.available}</span> disponibles
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminSystemStatus

